// libs
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// global components
import RouteChunkRenderer from 'shared/components/RouteChunkRenderer';
import RedirectToHomePage from 'shared/components/RedirectToHomePage/RedirectToHomePage';

// component
import SizeGuide from 'shared/layouts/SizeGuide';

// helpers
import { log } from 'shared/helpers/newRelic';
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';
import {
  notifyAppSizeSelected,
  notifyAppAddToBag,
} from 'shared/layouts/SizeGuide/Components/SizeGuideComponent/appCallbacks';
import { getWebAppType } from 'shared/helpers/deviceType/webAppType';

// selectors
import { getProductSizeOptions } from 'shared/store/details/selectors';

// constants
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { WEB_APP_TYPE } from 'shared/constants';
import {
  SIZE_GUIDE_SELECTION_APP_ANDROID_EXPERIMENT,
  SIZE_GUIDE_SELECTION_APP_IOS_EXPERIMENT,
  SIZE_GUIDE_SELECTION_APP_ANDROID_VARIANT,
  SIZE_GUIDE_SELECTION_APP_IOS_VARIANT,
} from 'shared/layouts/DetailsPage/components/ProductInfo/components/SizeOptions/texts';

const SIZE_GUIDE_MODULE = 'SIZE_GUIDE';

function SizeGuideAppContent() {
  const appType = getWebAppType();
  const experimentId = appType === WEB_APP_TYPE.IOS
    ? SIZE_GUIDE_SELECTION_APP_IOS_EXPERIMENT
    : SIZE_GUIDE_SELECTION_APP_ANDROID_EXPERIMENT;
  const expectedVariant = appType === WEB_APP_TYPE.IOS
    ? SIZE_GUIDE_SELECTION_APP_IOS_VARIANT
    : SIZE_GUIDE_SELECTION_APP_ANDROID_VARIANT;

  const { isEligible, variant: sizeGuideVariant } = useEligibleSSRVariantName(experimentId);
  const isSelectionEnabled = isEligible && sizeGuideVariant === expectedVariant;
  const sizeOptions = useSelector(getProductSizeOptions) as ProductDetail.IProduct['sizeOptions'];
  const [currentSize, setCurrentSize] = useState<ProductDetail.ISizeOption | undefined>(undefined);

  const outOfStockSizes = useMemo(
    () =>
      sizeOptions?.options
        ?.filter((opt: ProductDetail.ISizeOption) => opt.isOutOfStock === 1)
        .map((opt: ProductDetail.ISizeOption) => opt.sizeName) || [],
    [sizeOptions]
  );

  const stockRemainingMap = useMemo(
    () =>
      sizeOptions?.options?.reduce((acc: Record<string, number>, opt: ProductDetail.ISizeOption) => {
        if (opt.stock_remaining && opt.isOutOfStock !== 1) {
          acc[opt.sizeName] = opt.stock_remaining;
        }
        return acc;
      }, {}) || {},
    [sizeOptions]
  );

  const handleSizeSelect = useCallback(
    (sizeName: string) => {
      const matchingOption = sizeOptions?.options?.find(
        (opt: ProductDetail.ISizeOption) => opt.sizeName === sizeName
      );
      if (matchingOption) {
        setCurrentSize(matchingOption);
        notifyAppSizeSelected({
          sizeName: matchingOption.sizeName,
          sizeId: String(matchingOption.id),
          sku: matchingOption.sku,
          isOutOfStock: matchingOption.isOutOfStock === 1,
        });
      }
    },
    [sizeOptions]
  );

  const handleAddToBag = useCallback(() => {
    if (currentSize) {
      notifyAppAddToBag({
        sizeName: currentSize.sizeName,
        sizeId: String(currentSize.id),
        sku: currentSize.sku,
        vendorId: String(currentSize.vendor_id),
        vendorType: String(currentSize.vendor_type),
      });
    }
  }, [currentSize]);

  if (!isSelectionEnabled) {
    return <SizeGuide srcApp />;
  }

  return (
    <SizeGuide
      srcApp
      onSizeSelect={handleSizeSelect}
      selectedSize={currentSize}
      selectedSizeName={currentSize?.sizeName}
      addToCart={handleAddToBag}
      outOfStockSizes={outOfStockSizes}
      stockRemainingMap={stockRemainingMap}
    />
  );
}

function SizeGuideRoute() {
  useEffect(() => {
    try {
      // Store the original geolocation object
      const originalGeolocation = navigator.geolocation;

      // Disable geolocation permission requests
      Object.defineProperty(navigator, 'geolocation', {
        value: undefined,
        configurable: true, // So we can change it back later
      });

      // Clean up on unmount
      return () => {
        // Reset navigator.geolocation back to its original value
        Object.defineProperty(navigator, 'geolocation', {
          value: originalGeolocation,
          configurable: true,
        });
      };
    } catch (error) {
      log({
        errTitle: 'Malformed Adplatform Redirection',
        tags: { [TAG_IDENTIFIER.MODULE]: SIZE_GUIDE_MODULE },
        error: error,
      });
    }
  }, []);

  return (
    <RouteChunkRenderer
      mobileComponent={<SizeGuideAppContent />}
      desktopComponent={<RedirectToHomePage />}
    />
  );
}

export default memo(SizeGuideRoute);
